import { useEffect, useState } from "react";
import { Mail, Phone, Clock, MapPin, Instagram, Facebook, Twitter } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

const FAQ = [
  { q: "Cât durează livrarea?", a: "Livrarea națională se face prin curier în 1–3 zile lucrătoare de la confirmarea comenzii." },
  { q: "Ce metode de plată acceptați?", a: "Poți plăti online cu cardul, ramburs la livrare sau prin transfer bancar." },
  { q: "Cafeaua este proaspăt prăjită?", a: "Da, lucrăm cu loturi prăjite recent și ambalate sigilat pentru a păstra prospețimea." },
  { q: "Pot returna un produs?", a: "Da, ai la dispoziție 14 zile pentru retur, conform legislației în vigoare, dacă ambalajul este nedesfăcut." },
];

export default function Contact() {
  useEffect(() => window.scrollTo(0, 0), []);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="pt-20">
      <section className="bg-ink text-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="overline text-sand">Suntem aici pentru tine</p>
          <h1 className="font-display text-5xl md:text-8xl tracking-tight mt-3">Contact</h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid lg:grid-cols-[1fr_1.1fr] gap-16">
        {/* info */}
        <Reveal>
          <div className="space-y-8">
            <Info icon={Mail} label="Email" value="office@coffeecup-supply.com" />
            <Info icon={Phone} label="Telefon" value="0761 038 086" />
            <Info icon={Clock} label="Program" value="Luni–Vineri, 09:00–18:00" />
            <Info icon={MapPin} label="Sediu" value="Sat Lovrin, nr. 1236 (Hala 2), jud. Timiș, cod 307250" />
            <div className="pt-6 border-t border-line">
              <p className="overline text-ink/50 mb-4">Urmărește-ne</p>
              <div className="flex gap-4 text-ink/70">
                <a href="#" aria-label="Instagram" className="hover:text-roast"><Instagram /></a>
                <a href="#" aria-label="Facebook" className="hover:text-roast"><Facebook /></a>
                <a href="#" aria-label="Twitter" className="hover:text-roast"><Twitter /></a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nume" value={form.name} onChange={set("name")} testid="contact-name" />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" />
            </div>
            <Field label="Subiect" value={form.subject} onChange={set("subject")} testid="contact-subject" />
            <div>
              <label className="overline text-ink/50">Mesaj</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                data-testid="contact-message"
                className="mt-2 w-full bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="bg-ink text-cream px-9 py-4 hover:bg-roast transition-colors duration-300 text-sm font-semibold tracking-wide disabled:opacity-60"
            >
              {sending ? "Se trimite..." : "Trimite mesajul"}
            </button>
          </form>
        </Reveal>
      </section>

      {/* Hartă sediu */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20" data-testid="contact-map-section">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
            <div>
              <p className="overline text-roast">Ridicare de la sediu</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Vino la noi</h2>
              <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed">
                Sat Lovrin, nr. 1236 (Hala 2), jud. Timiș, cod poștal 307250. Te așteptăm de luni până vineri, 09:00–18:00.
              </p>
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Lovrin%2C%20Timi%C8%99%2C%20Rom%C3%A2nia"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions-link"
              className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3.5 text-sm font-semibold tracking-wide hover:bg-roast transition-colors whitespace-nowrap"
            >
              <MapPin size={16} /> Deschide în Google Maps
            </a>
          </div>
          <div className="aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden border border-line bg-stone">
            <iframe
              title="Sediu Coffee Cup Supply — Lovrin, Timiș"
              src="https://maps.google.com/maps?q=Lovrin%2C%20Timi%C8%99%2C%20Rom%C3%A2nia&z=13&output=embed"
              className="h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map"
            />
          </div>
        </Reveal>
      </section>

      <section className="bg-stone py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="overline text-roast text-center">Întrebări frecvente</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-center mt-3 mb-12">FAQ</h2>
          </Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-line">
                <AccordionTrigger className="font-display text-2xl text-left hover:no-underline hover:text-roast py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="h-11 w-11 grid place-items-center border border-line text-roast shrink-0">
      <Icon size={18} />
    </div>
    <div>
      <p className="overline text-ink/40">{label}</p>
      <p className="mt-1">{value}</p>
    </div>
  </div>
);

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="overline text-ink/50">{label}</label>
    <input
      required
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-2 w-full bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors"
    />
  </div>
);
